import type { FC } from 'react';
import { useState } from 'react';
import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import PageLink from '@/utils/PageLink';

const METHOD_CHIP: Record<string, string> = {
  GET: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  POST: 'bg-primary-50 text-primary-700 ring-primary-200',
  PATCH: 'bg-amber-50 text-amber-700 ring-amber-200',
  PUT: 'bg-amber-50 text-amber-700 ring-amber-200',
  DELETE: 'bg-rose-50 text-rose-700 ring-rose-200',
};

/**
 * Compact endpoint listing: method chip, path, required scope, one-line summary.
 * The scope column earns its place — a missing scope, not a wrong path, is the
 * usual reason a correct-looking Management API request comes back 403.
 */
const ApiEndpointTable: FC<{ rows: string[][] }> = ({ rows }) => (
  <div className="my-6 overflow-hidden rounded-lg border border-gray-200">
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            {['Method', 'Path', 'Scope', 'Description'].map((heading) => (
              <th
                key={heading}
                className="border-b border-gray-200 px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                {heading}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([method, path, scope, description]) => (
            <tr key={`${method}-${path}`}>
              <td className="border-b border-gray-100 px-4 py-2.5 align-top">
                <span
                  className={`inline-block rounded px-1.5 py-0.5 font-mono text-[11px] font-bold ring-1 ${
                    METHOD_CHIP[method] || 'bg-gray-50 text-gray-700 ring-gray-200'
                  }`}>
                  {method}
                </span>
              </td>
              <td className="whitespace-nowrap border-b border-gray-100 px-4 py-2.5 align-top font-mono text-[13px] text-gray-900">
                {path}
              </td>
              <td className="whitespace-nowrap border-b border-gray-100 px-4 py-2.5 align-top font-mono text-[12px] text-gray-500">
                {scope}
              </td>
              <td className="border-b border-gray-100 px-4 py-2.5 align-top text-gray-700">
                {description}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const ApiDocsPage: FC = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: '1. Overview' },
    { id: 'authentication', title: '2. Authentication' },
    { id: 'api-key-management', title: '3. API Key Management' },
    { id: 'client-api', title: '4. Chat Endpoints' },
    { id: 'management-api', title: '5. Management API' },
    { id: 'streaming', title: '6. Streaming Responses' },
    { id: 'error-handling', title: '7. Error Handling' },
    { id: 'rate-limiting', title: '8. Rate Limiting' },
    { id: 'code-examples', title: '9. Code Examples' },
  ];

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <NextSeo
        title="API Documentation"
        description="Complete API documentation for integrating Chat.co chatbots into your applications. Learn about authentication, endpoints, streaming, and code examples."
      />
      <Header />

      {/* Hero Section */}
      <div className="border-b border-gray-200 bg-gray-50">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-md bg-primary-100 px-3 py-1.5">
              <Icon icon="tabler:code" className="text-base text-primary-700" />
              <span className="text-xs font-semibold uppercase tracking-wider text-primary-700">
                Developer
              </span>
            </div>
            <h1 className="mb-4 text-5xl font-bold tracking-tight text-gray-900">
              API Documentation
            </h1>
            <p className="text-lg text-gray-600">
              Version 1.0 | Base URL:{' '}
              <code className="rounded bg-gray-200 px-2 py-0.5 font-mono text-sm">
                https://api.chat.co
              </code>
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex gap-12 py-12">
          {/* Sidebar Navigation */}
          <aside className="hidden w-64 flex-shrink-0 lg:block">
            <div className="sticky top-24">
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className={`block w-full rounded-md px-3 py-2 text-left text-sm font-medium transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}>
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <main className="max-w-3xl flex-1">
            <div className="prose prose-slate max-w-none">
              {/* Section 1: Overview */}
              <section id="overview" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">1. Overview</h2>
                <p className="text-base leading-relaxed text-gray-700">
                  The Chat.co API allows you to programmatically integrate your chatbots into custom
                  applications, websites, and workflows. With this API, you can create and manage
                  conversations, send messages, receive AI-powered responses, and stream responses
                  in real-time.
                </p>

                <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-5">
                  <p className="text-sm font-medium text-blue-900">
                    <strong>Looking for AI agent integrations?</strong> This page covers the direct
                    HTTP API. If you want Claude Desktop, Cursor, or another MCP-compatible client
                    to work with Chat.co, start with{' '}
                    <Link href={PageLink.docsApiVsMcp} className="underline">
                      API vs MCP
                    </Link>{' '}
                    and{' '}
                    <Link href={PageLink.docsMcpAgentSetup} className="underline">
                      MCP Agent Setup
                    </Link>
                    .
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">1.1 Key Features</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Feature
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-700">
                          RESTful API
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Standard HTTP methods (GET, POST, DELETE)
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-700">
                          Streaming Support
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Real-time response streaming via SSE or plain text
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-700">
                          Scoped API Keys
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Keys can be restricted to specific chatbots for security
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-700">
                          Citation Support
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          AI responses include source citations when available
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-700">
                          Document Context
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Attach uploaded documents to enhance AI responses
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">1.2 Getting Started</h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  To use the direct Chat.co API, you need to:
                </p>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Create a Chat.co account and set up a chatbot</li>
                  <li>Generate an API key from your Settings page</li>
                  <li>Assign the API key to a specific chatbot</li>
                  <li>Use the API key in your application&apos;s requests</li>
                </ul>
              </section>

              {/* Section 2: Authentication */}
              <section id="authentication" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">2. Authentication</h2>

                <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-5">
                  <p className="text-sm font-medium text-amber-900">
                    <strong>Important:</strong> MCP uses a different onboarding flow than the direct
                    API. Instead of manually creating a dashboard key and copying it into your
                    client, MCP typically uses agent login with browser approval. See{' '}
                    <Link href={PageLink.docsMcpAgentSetup} className="underline">
                      MCP Agent Setup
                    </Link>
                    .
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">2.1 Two kinds of key</h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  Chat.co issues two key types, and choosing the wrong one is the most common
                  security mistake teams make with this API.
                </p>

                <div className="my-6 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-lg border border-gray-200 bg-white p-5">
                    <code className="font-mono text-sm font-semibold text-gray-900">sk_live_…</code>
                    <p className="mt-1 text-sm font-medium text-gray-900">Secret key</p>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600">
                      Full-power server-side credential. Carries whatever scopes you grant it, up to
                      and including creating and deleting bots. <strong>Never</strong> ship one to a
                      browser, a mobile app, or anything a user can read.
                    </p>
                  </div>
                  <div className="rounded-lg border border-gray-200 bg-white p-5">
                    <code className="font-mono text-sm font-semibold text-gray-900">pk_live_…</code>
                    <p className="mt-1 text-sm font-medium text-gray-900">Publishable key</p>
                    <p className="mt-2 text-sm leading-relaxed text-gray-600">
                      Browser-safe, bound to a single chatbot, and permanently locked to{' '}
                      <code>chat:read</code> and <code>chat:write</code> — those scopes are forced
                      at mint time and re-forced on every request. It can hold a conversation and
                      nothing else.
                    </p>
                  </div>
                </div>

                <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-5">
                  <p className="text-sm leading-relaxed text-amber-900">
                    <strong>Publishable keys are visible credentials.</strong> They are embedded in
                    pages your visitors load, and their domain restriction is enforced from the{' '}
                    <code>Origin</code>/<code>Referer</code> headers — which anything outside a
                    browser can set freely. Treat a <code>pk_live_</code> as public: fine for the
                    embedded chat it was designed for, never a substitute for a secret key.
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">2.2 Scopes</h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  Every secret key carries an explicit scope list. A request whose key lacks the
                  required scope is rejected with <code>403</code>, even if the key is otherwise
                  valid and the chatbot is accessible — this is the single most common cause of a
                  correct-looking request failing.
                </p>

                <div className="my-6 overflow-x-auto rounded-lg border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b border-gray-200 px-4 py-2.5 text-left font-semibold text-gray-900">
                          Scope
                        </th>
                        <th className="border-b border-gray-200 px-4 py-2.5 text-left font-semibold text-gray-900">
                          Grants
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['chat:read', 'Read conversations and messages'],
                        ['chat:write', 'Create conversations and send messages'],
                        ['bot:read', 'Read chatbot details and configuration'],
                        ['bot:write', 'Create, update and delete chatbots; change configuration'],
                        ['content:read', 'Read Q&A pairs and text training'],
                        ['content:write', 'Create, update and delete Q&A pairs and text training'],
                        ['crawl:read', 'Read crawl jobs, crawled pages and document jobs'],
                        ['crawl:write', 'Start and cancel crawls; upload and delete documents'],
                        [
                          'analytics:read',
                          'Read analytics, leads, knowledge gaps and message allocation',
                        ],
                      ].map(([scope, grants]) => (
                        <tr key={scope}>
                          <td className="whitespace-nowrap border-b border-gray-100 px-4 py-2.5 font-mono text-[13px] text-gray-900">
                            {scope}
                          </td>
                          <td className="border-b border-gray-100 px-4 py-2.5 text-gray-700">
                            {grants}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p className="text-base leading-relaxed text-gray-700">
                  A key created without an explicit scope list defaults to <code>chat:read</code>{' '}
                  and <code>chat:write</code> only. Grant the narrowest set that does the job — a
                  key that only reads analytics should not be able to delete a bot.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  2.3 Authorization Header
                </h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  Include your API key in the{' '}
                  <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-sm">
                    Authorization
                  </code>{' '}
                  header of every request:
                </p>
                <div className="rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <code className="text-sm text-green-400">
                    Authorization: Bearer sk_live_your_api_key_here
                  </code>
                </div>
                <p className="mt-4 text-base leading-relaxed text-gray-700">
                  You can also send the key without the Bearer prefix:
                </p>
                <div className="rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <code className="text-sm text-green-400">
                    Authorization: sk_live_your_api_key_here
                  </code>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  2.4 Security Best Practices
                </h3>
                <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-5">
                  <p className="text-sm font-medium text-amber-900">
                    <strong>Important:</strong> Never expose API keys in client-side code
                    (JavaScript, mobile apps). Store keys in environment variables or secure vaults.
                  </p>
                </div>
                <ul className="ml-6 list-disc space-y-2 text-base leading-relaxed text-gray-700">
                  <li>Use chatbot-scoped keys when possible to limit access</li>
                  <li>Set expiration dates for temporary integrations</li>
                  <li>Rotate keys periodically</li>
                  <li>Delete unused keys promptly</li>
                  <li>Never commit API keys to version control</li>
                </ul>
              </section>

              {/* Section 3: API Key Management */}
              <section id="api-key-management" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">3. API Key Management</h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  These endpoints require <strong>JWT authentication</strong> (your user session
                  from the dashboard), not API key authentication.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">3.1 Generate API Key</h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  Creates a new API key for your account.
                </p>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <code className="text-sm text-blue-400">POST /api-keys</code>
                </div>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">Request Body</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`{
  "name": "My Integration Key",
  "expiresAt": "2026-12-31T23:59:59Z",
  "chatbotId": "507f1f77bcf86cd799439011"
}`}</pre>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Field
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Type
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Required
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          name
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">string</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">No</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          User-friendly name (max 100 chars)
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          expiresAt
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          ISO 8601 date
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">No</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Expiration date (must be in future)
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          chatbotId
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">string</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">No</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Chatbot ID to scope the key
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  Response (201 Created)
                </h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`{
  "success": true,
  "message": "API key generated successfully...",
  "data": {
    "id": "507f1f77bcf86cd799439011",
    "key": "sk_live_abcd1234efgh5678ijkl9012mnop3456qrst7890uvwx",
    "keyPrefix": "sk_live_abcd1234efgh5678...",
    "name": "My Integration Key",
    "expiresAt": "2026-12-31T23:59:59.000Z",
    "createdAt": "2025-11-10T12:00:00.000Z"
  }
}`}</pre>
                </div>

                <div className="my-6 rounded-lg border-l-4 border-red-500 bg-red-50 p-5">
                  <p className="text-sm font-medium text-red-900">
                    <strong>Warning:</strong> The full{' '}
                    <code className="rounded bg-red-100 px-1">key</code> value is shown only once.
                    Store it securely immediately.
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">3.2 List API Keys</h3>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <code className="text-sm text-blue-400">GET /api-keys?page=1&limit=10</code>
                </div>
                <p className="text-base leading-relaxed text-gray-700">
                  Returns a paginated list of your API keys (prefixes only, not full keys).
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">3.3 Delete API Key</h3>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <code className="text-sm text-red-400">DELETE /api-keys/:id</code>
                </div>
                <p className="text-base leading-relaxed text-gray-700">
                  Soft-deletes an API key. The key will immediately become invalid.
                </p>
              </section>

              {/* Section 4: Client API Endpoints */}
              <section id="client-api" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">4. Chat Endpoints</h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  All Client API endpoints require <strong>API key authentication</strong> and are
                  prefixed with{' '}
                  <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-sm">
                    /client/v1
                  </code>
                  .
                </p>

                <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-5">
                  <p className="text-sm font-medium text-blue-900">
                    <strong>Important:</strong> Your API key must be associated with a chatbot. Keys
                    without a chatbot assignment will receive a{' '}
                    <code className="rounded bg-blue-100 px-1">CHATBOT_ACCESS_DENIED</code> error.
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.1 Get Chatbot Details
                </h3>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <code className="text-sm text-green-400">GET /client/v1/chatbot</code>
                </div>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  Retrieves the chatbot configuration and appearance settings associated with your
                  API key.
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">Response</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`{
  "success": true,
  "data": {
    "chatbot": {
      "id": "507f1f77bcf86cd799439011",
      "name": "Customer Support Bot",
      "isPublic": true,
      "appearance": {
        "title": "Support Assistant",
        "welcomeMessage": "Hello! How can I help you today?",
        "placeholderText": "Type your question...",
        "suggestions": [
          { "value": "What are your hours?" }
        ],
        "enableDocumentUpload": true,
        "showCitations": true
      },
      "createdAt": "2025-01-15T10:00:00.000Z"
    }
  }
}`}</pre>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.2 List Conversations
                </h3>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <code className="text-sm text-green-400">GET /client/v1/conversations</code>
                </div>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">Query Parameters</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Parameter
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Type
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Default
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          page
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          integer
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">1</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Page number
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          limit
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          integer
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">20</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Items per page (max 100)
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          sortBy
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">string</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          createdAt
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          createdAt or updatedAt
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          sortOrder
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">string</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">desc</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          asc or desc
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          startDate
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          ISO 8601
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">-</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Filter from this date
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          endDate
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          ISO 8601
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">-</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Filter until this date
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.3 Create Conversation
                </h3>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <code className="text-sm text-yellow-400">POST /client/v1/conversations</code>
                </div>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">Request Body</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`{
  "metadata": {
    "source": "mobile-app",
    "clientId": "user-12345"
  }
}`}</pre>
                </div>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  Response (201 Created)
                </h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`{
  "success": true,
  "data": {
    "conversation": {
      "id": "507f1f77bcf86cd799439011",
      "chatbotId": "507f1f77bcf86cd799439012",
      "chatbot": {
        "id": "507f1f77bcf86cd799439012",
        "name": "Customer Support Bot"
      },
      "createdAt": "2025-11-10T10:00:00.000Z"
    }
  }
}`}</pre>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">4.4 Get Conversation</h3>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <code className="text-sm text-green-400">
                    GET /client/v1/conversations/:conversationId
                  </code>
                </div>
                <p className="text-base leading-relaxed text-gray-700">
                  Retrieves a conversation with all its messages.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.5 Send Message (Non-Streaming)
                </h3>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <code className="text-sm text-yellow-400">
                    POST /client/v1/conversations/:conversationId/messages
                  </code>
                </div>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">Request Body</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`{
  "message": "What are your business hours?",
  "documentIds": ["507f1f77bcf86cd799439015"]
}`}</pre>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Field
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Type
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Required
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          message
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">string</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">Yes</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Message content (max 8,000 chars)
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          documentIds
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">array</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">No</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          IDs of uploaded documents
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">Response</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`{
  "success": true,
  "data": {
    "userMessage": {
      "id": "507f1f77bcf86cd799439016",
      "content": "What are your business hours?",
      "isFromUser": true,
      "createdAt": "2025-11-10T10:16:00.000Z"
    },
    "botResponse": {
      "id": "507f1f77bcf86cd799439017",
      "content": "Our business hours are Monday through Friday, 9:00 AM to 5:00 PM EST.",
      "isFromUser": false,
      "createdAt": "2025-11-10T10:16:03.000Z",
      "citations": [
        {
          "title": "Contact Information",
          "url": "https://example.com/contact",
          "snippet": "Hours of operation: Mon-Fri 9AM-5PM EST"
        }
      ]
    }
  }
}`}</pre>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  4.6 Send Message (Streaming)
                </h3>
                <div className="mb-4 rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <code className="text-sm text-yellow-400">
                    POST /client/v1/conversations/:conversationId/messages/stream
                  </code>
                </div>
                <p className="text-base leading-relaxed text-gray-700">
                  Sends a message and receives the AI response as a real-time stream. See Section 6
                  for streaming details.
                </p>
              </section>

              {/* Section 5: Management API */}
              <section id="management-api" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">5. Management API</h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  The endpoints in Section 4 are the chat surface: they let a key already bound to
                  one chatbot hold conversations. The Management API is everything else — creating
                  bots, loading knowledge into them, and reading what comes back out. These routes
                  are addressed by chatbot ID, so a single secret key can operate across every bot
                  it has access to.
                </p>

                <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-5">
                  <p className="text-sm leading-relaxed text-amber-900">
                    <strong>Secret keys only.</strong> Every route below requires a scope beyond{' '}
                    <code>chat:*</code>, so a publishable key cannot reach any of them.
                  </p>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">5.1 Chatbots</h3>
                <ApiEndpointTable
                  rows={[
                    [
                      'GET',
                      '/client/v1/chatbots',
                      'bot:read',
                      'List every chatbot this key can reach',
                    ],
                    ['GET', '/client/v1/chatbots/:chatbotId', 'bot:read', 'Get one chatbot'],
                    ['POST', '/client/v1/chatbots', 'bot:write', 'Create a chatbot'],
                    [
                      'PATCH',
                      '/client/v1/chatbots/:chatbotId',
                      'bot:write',
                      'Rename, or change visibility',
                    ],
                    [
                      'DELETE',
                      '/client/v1/chatbots/:chatbotId',
                      'bot:write',
                      'Soft-delete a chatbot',
                    ],
                  ]}
                />

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">5.2 Configuration</h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  Paths below are relative to{' '}
                  <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-sm">
                    /client/v1/chatbots/:chatbotId
                  </code>
                  .
                </p>
                <ApiEndpointTable
                  rows={[
                    [
                      'GET',
                      '/config',
                      'bot:read',
                      'Full sanitized configuration — never includes provider API keys',
                    ],
                    ['PATCH', '/temperature', 'bot:write', 'Set response creativity'],
                    ['PATCH', '/text-prompt', 'bot:write', 'Replace the system instruction prompt'],
                    [
                      'PATCH',
                      '/appearance',
                      'bot:write',
                      'Widget appearance, voice and citation settings',
                    ],
                    [
                      'PATCH',
                      '/domain',
                      'bot:write',
                      'Allowed embed domains and per-domain rate limits',
                    ],
                    ['GET', '/whitelist', 'bot:read', 'Read email whitelist settings'],
                    [
                      'POST',
                      '/whitelist/emails',
                      'bot:write',
                      'Add an email to the whitelist (private bots)',
                    ],
                    [
                      'DELETE',
                      '/whitelist/emails',
                      'bot:write',
                      'Remove an email from the whitelist',
                    ],
                    ['PATCH', '/whitelist', 'bot:write', 'Turn whitelist enforcement on or off'],
                  ]}
                />

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  5.3 Documents &amp; crawling
                </h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  Uploading is a three-step flow — presign, PUT the bytes straight to S3, then
                  finalize. The file never transits the Chat.co API, which is what makes large
                  uploads workable. Crawls pause after discovery so you choose which pages to keep.
                </p>
                <ApiEndpointTable
                  rows={[
                    ['GET', '/documents', 'crawl:read', 'List uploaded document jobs'],
                    [
                      'GET',
                      '/documents/check-existing',
                      'crawl:read',
                      'Check whether a file name was already uploaded',
                    ],
                    [
                      'POST',
                      '/documents/presign-upload',
                      'crawl:write',
                      'Reserve storage, get a presigned S3 PUT URL',
                    ],
                    [
                      'POST',
                      '/documents/finalize-upload',
                      'crawl:write',
                      'Turn a completed upload into a processing job',
                    ],
                    [
                      'GET',
                      '/documents/:jobId',
                      'crawl:read',
                      'Processing status for one document',
                    ],
                    ['DELETE', '/documents', 'crawl:write', 'Delete document jobs'],
                    ['POST', '/crawl/jobs', 'crawl:write', 'Start a crawl'],
                    ['GET', '/crawl/jobs', 'crawl:read', 'List crawl jobs'],
                    ['GET', '/crawl/jobs/:jobId', 'crawl:read', 'Crawl job status'],
                    ['POST', '/crawl/jobs/:jobId/cancel', 'crawl:write', 'Cancel a running crawl'],
                    ['GET', '/crawl/jobs/:jobId/pages', 'crawl:read', 'Pages a crawl stored'],
                    [
                      'GET',
                      '/crawl/jobs/:jobId/pending-pages',
                      'crawl:read',
                      'Discovered pages awaiting selection',
                    ],
                    [
                      'POST',
                      '/crawl/jobs/:jobId/select-pages',
                      'crawl:write',
                      'Choose which discovered pages to index',
                    ],
                    [
                      'POST',
                      '/crawl/jobs/:jobId/cancel-selection',
                      'crawl:write',
                      'Discard the pending pages',
                    ],
                  ]}
                />

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  5.4 Q&amp;A and text training
                </h3>
                <ApiEndpointTable
                  rows={[
                    ['GET', '/qna', 'content:read', 'List Q&A pairs'],
                    ['POST', '/qna', 'content:write', 'Create a Q&A pair'],
                    ['POST', '/qna/bulk', 'content:write', 'Create many Q&A pairs in one call'],
                    ['GET', '/qna/:qnaId', 'content:read', 'Get one Q&A pair'],
                    ['PATCH', '/qna/:qnaId', 'content:write', 'Update a Q&A pair'],
                    ['DELETE', '/qna/:qnaId', 'content:write', 'Delete a Q&A pair'],
                    [
                      'GET',
                      '/text-training/status',
                      'content:read',
                      'Whether text training content exists',
                    ],
                    [
                      'PUT',
                      '/text-training',
                      'content:write',
                      'Create or replace the text training body',
                    ],
                    ['DELETE', '/text-training', 'content:write', 'Remove text training content'],
                  ]}
                />

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  5.5 Analytics, leads &amp; knowledge gaps
                </h3>
                <ApiEndpointTable
                  rows={[
                    [
                      'GET',
                      '/client/v1/message-allocation',
                      'analytics:read',
                      'Remaining message credits for the key owner',
                    ],
                    [
                      'POST',
                      '/client/v1/chatbots/:chatbotId/analytics',
                      'analytics:read',
                      'Analytics for one chatbot',
                    ],
                    [
                      'GET',
                      '/client/v1/chatbots/:chatbotId/leads',
                      'analytics:read',
                      'Leads captured by one chatbot',
                    ],
                    [
                      'GET',
                      '/client/v1/chatbots/:chatbotId/knowledge-gaps',
                      'analytics:read',
                      'Questions the bot could not answer',
                    ],
                    [
                      'POST',
                      '/client/v1/chatbots/:chatbotId/knowledge-gaps/suggest-answer',
                      'analytics:read',
                      'Draft an answer for a detected gap',
                    ],
                    [
                      'POST',
                      '/client/v1/chatbots/:chatbotId/knowledge-gaps/create-qna',
                      'content:write',
                      'Promote a gap straight into a Q&A pair',
                    ],
                  ]}
                />

                <div className="my-6 rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5">
                  <p className="text-sm leading-relaxed text-primary-900">
                    Driving these endpoints from an AI client — Claude Desktop, Claude Code, Cursor
                    — is usually easier through MCP than through raw HTTP. The Chat.co MCP server
                    wraps this same surface as tools. See{' '}
                    <Link href={PageLink.docsMcpAgentSetup} className="underline">
                      MCP Agent Setup
                    </Link>{' '}
                    and{' '}
                    <Link href={PageLink.docsApiVsMcp} className="underline">
                      API vs MCP
                    </Link>
                    .
                  </p>
                </div>
              </section>

              {/* Section 6: Streaming */}
              <section id="streaming" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">6. Streaming Responses</h2>
                <p className="mb-6 text-base leading-relaxed text-gray-700">
                  The streaming endpoint supports two formats via the{' '}
                  <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-sm">format</code>{' '}
                  query parameter.
                </p>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.1 Plain Text Format (Default)
                </h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  Best for simple integrations and testing with tools like Postman.
                </p>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`[START:507f1f77bcf86cd799439016]
Our return policy allows you to return most items within 30 days of purchase.

1. Items must be in original condition
2. You'll need the original receipt

[COMPLETE]
{"botMessageId":"507f1f77bcf86cd799439017","citations":[...]}`}</pre>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Marker
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          [START:id]
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Stream started, includes user message ID
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          &lt;content&gt;
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Streamed response text (arrives incrementally)
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          [COMPLETE]
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Stream finished successfully
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          [ERROR]
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          An error occurred during streaming
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  6.2 Server-Sent Events (SSE) Format
                </h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  Best for JavaScript applications using the EventSource API. Use{' '}
                  <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-sm">
                    ?format=sse
                  </code>
                  .
                </p>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`data: {"type":"start","userMessageId":"507f1f77bcf86cd799439016"}

data: {"type":"content","content":"Our return policy"}

data: {"type":"content","content":" allows you to return"}

data: {"type":"complete","botMessageId":"507f1f77bcf86cd799439017","citations":[]}`}</pre>
                </div>

                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Event Type
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Fields
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          start
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          userMessageId
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Stream started
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          content
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          content
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Text chunk to append
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          complete
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          botMessageId, citations
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Stream finished
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          error
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">error</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Error message
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Section 6: Error Handling */}
              <section id="error-handling" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">7. Error Handling</h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">7.1 Response Format</h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  All API responses follow a consistent format:
                </p>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">Success Response</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`{
  "success": true,
  "data": { ... }
}`}</pre>
                </div>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">Error Response</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`{
  "success": false,
  "error": {
    "code": "ERROR_CODE",
    "message": "Human-readable error description"
  }
}`}</pre>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">7.2 Error Codes</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Code
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          HTTP Status
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          INVALID_API_KEY
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">401</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          API key is missing, malformed, expired, or invalid
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          RATE_LIMIT_EXCEEDED
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          401/429
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Too many failed attempts or message rate limit
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          CHATBOT_NOT_FOUND
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">404</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Chatbot does not exist
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          CHATBOT_ACCESS_DENIED
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">403</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          API key not associated with chatbot
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          CHATBOT_FROZEN
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">403</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Chatbot is frozen due to plan downgrade
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          CONVERSATION_NOT_FOUND
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">404</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Conversation does not exist
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          CONVERSATION_ACCESS_DENIED
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">403</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          No access to this conversation
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          MESSAGE_LIMIT_EXCEEDED
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">403</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Monthly message credit limit reached
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          INVALID_REQUEST
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">400</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Request body validation failed
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 font-mono text-xs text-gray-700">
                          INTERNAL_ERROR
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">500</td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Unexpected server error
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Section 7: Rate Limiting */}
              <section id="rate-limiting" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">8. Rate Limiting</h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  8.1 Authentication Rate Limits
                </h3>
                <p className="mb-4 text-base leading-relaxed text-gray-700">
                  To protect against brute-force attacks, authentication is rate-limited:
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Limit
                        </th>
                        <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Failed attempts
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          5 per IP address
                        </td>
                      </tr>
                      <tr>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          Lockout duration
                        </td>
                        <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                          15 minutes
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  8.2 Message Rate Limits
                </h3>
                <p className="text-base leading-relaxed text-gray-700">
                  Message rate limits are based on your subscription plan. Exceeding your monthly
                  message limit returns{' '}
                  <code className="rounded bg-gray-100 px-2 py-0.5 font-mono text-sm">
                    MESSAGE_LIMIT_EXCEEDED
                  </code>
                  .
                </p>
              </section>

              {/* Section 8: Code Examples */}
              <section id="code-examples" className="mb-16 scroll-mt-24">
                <h2 className="mb-6 text-3xl font-bold text-gray-900">9. Code Examples</h2>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">9.1 cURL Examples</h3>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">Get Chatbot</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`curl -X GET "https://api.chat.co/client/v1/chatbot" \\
  -H "Authorization: Bearer sk_live_your_api_key"`}</pre>
                </div>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">
                  Create Conversation
                </h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`curl -X POST "https://api.chat.co/client/v1/conversations" \\
  -H "Authorization: Bearer sk_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{}'`}</pre>
                </div>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">Send Message</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`curl -X POST "https://api.chat.co/client/v1/conversations/CONVERSATION_ID/messages" \\
  -H "Authorization: Bearer sk_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Hello, I need help"}'`}</pre>
                </div>

                <h4 className="mb-3 mt-6 text-lg font-semibold text-gray-900">Stream Message</h4>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`curl -X POST "https://api.chat.co/client/v1/conversations/CONVERSATION_ID/messages/stream?format=plain" \\
  -H "Authorization: Bearer sk_live_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Tell me about your products"}' \\
  --no-buffer`}</pre>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">9.2 Node.js Example</h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`const axios = require('axios');

const API_KEY = 'sk_live_your_api_key';
const BASE_URL = 'https://api.chat.co/client/v1';

const client = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Authorization': \`Bearer \${API_KEY}\`,
    'Content-Type': 'application/json'
  }
});

async function chat() {
  // 1. Create a new conversation
  const { data: convResponse } = await client.post('/conversations');
  const conversationId = convResponse.data.conversation.id;
  console.log('Created conversation:', conversationId);

  // 2. Send a message
  const { data: msgResponse } = await client.post(
    \`/conversations/\${conversationId}/messages\`,
    { message: 'What services do you offer?' }
  );

  console.log('Bot response:', msgResponse.data.botResponse.content);
  console.log('Citations:', msgResponse.data.botResponse.citations);
}

chat();`}</pre>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">9.3 Python Example</h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`import requests

API_KEY = 'sk_live_your_api_key'
BASE_URL = 'https://api.chat.co/client/v1'

headers = {
    'Authorization': f'Bearer {API_KEY}',
    'Content-Type': 'application/json'
}

# Create conversation
response = requests.post(f'{BASE_URL}/conversations', headers=headers, json={})
conversation_id = response.json()['data']['conversation']['id']
print(f'Created conversation: {conversation_id}')

# Send message
response = requests.post(
    f'{BASE_URL}/conversations/{conversation_id}/messages',
    headers=headers,
    json={'message': 'What are your pricing plans?'}
)

result = response.json()
print(f"Bot: {result['data']['botResponse']['content']}")`}</pre>
                </div>

                <h3 className="mb-4 mt-8 text-xl font-bold text-gray-900">
                  9.4 Python Streaming Example
                </h3>
                <div className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
                  <pre className="text-sm text-gray-300">{`import requests

API_KEY = 'sk_live_your_api_key'
BASE_URL = 'https://api.chat.co/client/v1'

headers = {
    'Authorization': f'Bearer {API_KEY}',
    'Content-Type': 'application/json'
}

conversation_id = 'your_conversation_id'

# Stream response
response = requests.post(
    f'{BASE_URL}/conversations/{conversation_id}/messages/stream?format=plain',
    headers=headers,
    json={'message': 'Explain your features in detail'},
    stream=True
)

for chunk in response.iter_content(chunk_size=None, decode_unicode=True):
    if chunk:
        print(chunk, end='', flush=True)`}</pre>
                </div>

                {/* Support Section */}
                <div className="mt-12 rounded-lg border border-gray-200 bg-gray-50 p-6">
                  <h3 className="mb-4 text-xl font-bold text-gray-900">Need Help?</h3>
                  <p className="mb-4 text-base leading-relaxed text-gray-700">
                    If you have questions or need assistance with the API, our team is here to help.
                  </p>
                  <p className="text-base text-gray-700">
                    <strong>Email:</strong>{' '}
                    <a
                      href="mailto:support@chat.co"
                      className="text-primary-600 hover:text-primary-700">
                      support@chat.co
                    </a>
                  </p>
                </div>

                <div className="mt-8 border-t border-gray-200 pt-8 text-center">
                  <p className="text-sm text-gray-500">
                    &copy; 2026 SA Cyber LLC d/b/a Chat.co. All rights reserved.
                  </p>
                </div>
              </section>
            </div>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ApiDocsPage;
